<template>
  <Modal
    :modal-style="{ maxWidth: '90vw' }"
    :title="importLabel"
  >
    <div
      v-if="step === 1"
      class="preview-body scroll"
    >
      <div class="row q-pa-md q-gutter-md">
        <QInput
          accept=".csv"
          class="col-6"
          dense
          filled
          hint="Upload CSV"
          square
          type="file"
          @update:model-value="processCsvFile"
        />
        <VueJsonCsv
          class="col-5"
          :data="sampleCSV"
          name="sample.csv"
        >
          <QBtn
            color="primary"
            flat
            label="Sample.csv"
            no-caps
          />
        </VueJsonCsv>
      </div>
    </div>

    <div v-if="step === 2">
      <div class="row">
        <QMarkupTable
          class="full-width full-height"
          style="height: 600px"
        >
          <thead>
            <tr>
              <th class="column-label text-left">Category</th>
              <th class="column-label text-left">Label</th>
              <th class="column-label text-left">Notes</th>
              <th class="column-query text-left">Boolean Query</th>
              <th class="column-checkbox text-left">Valid?</th>
              <template v-if="!topicQuery">
                <th class="column-checkbox text-left">Case Sensitive</th>
                <th class="column-checkbox text-left">Print</th>
                <th class="column-checkbox text-left">Online</th>
                <th class="column-checkbox text-left">Magazine</th>
                <th class="column-checkbox text-left">Podcast</th>
                <th class="column-checkbox text-left">TV</th>
                <th class="column-checkbox text-left">Radio</th>
                <th class="column-checkbox text-left">Social</th>
                <th class="column-checkbox text-left">FilterSets</th>
                <th class="column-checkbox text-left">International Plus</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(query, index) in queries"
              :key="index"
            >
              <td class="column-label text-left">
                <QInput
                  v-model="query.category"
                  dense
                  filled
                  square
                />
              </td>
              <td class="column-label text-left">
                <QInput
                  v-model="query.label"
                  dense
                  filled
                  square
                />
              </td>
              <td class="column-label text-left">
                <QInput
                  v-model="query.notes"
                  dense
                  filled
                  square
                />
              </td>
              <td class="column-query text-left">
                <QInput
                  v-model="query.boolean_query"
                  debounce="500"
                  filled
                  rows="1"
                  square
                  type="textarea"
                  @update:model-value="validateQuery(query)"
                />
                <p
                  v-if="!query.valid"
                  class="text-ds-red-5"
                >
                  {{ query.validationMessage }}
                </p>
              </td>
              <td class="column-checkbox text-left">
                <QSpinner v-if="query.loading" />
                <QCheckbox
                  v-if="!query.loading"
                  v-model="query.valid"
                  disable
                />
              </td>
              <template v-if="!topicQuery">
                <td class="column-checkbox text-left">
                  <QCheckbox v-model="query.case_sensitive" />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox v-model="query.print_content" />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox v-model="query.online_content" />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox v-model="query.magazine_content" />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox v-model="query.podcast_content" />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox v-model="query.tv_content" />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox v-model="query.radio_content" />
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox v-model="query.social_content" />
                </td>
                <td class="column-checkbox text-left">
                  {{ query.filter_set_ids }}
                </td>
                <td class="column-checkbox text-left">
                  <QCheckbox v-model="query.international_plus" />
                </td>
              </template>
            </tr>
          </tbody>
        </QMarkupTable>
      </div>
    </div>

    <template #buttons>
      <div class="full-width flex justify-between">
        <div>
          <QBtn
            v-if="step === 2"
            color="primary"
            flat
            label="Back"
            no-caps
            @click="step = 1"
          />
        </div>
        <div>
          <QBtn
            v-close-popup
            color="primary"
            flat
            label="Cancel"
            no-caps
            @click="cancel"
          />
          <QBtn
            v-if="step === 1"
            color="black"
            :label="validateLabel"
            no-caps
            @click="validateQueries"
          />
          <QBtn
            v-if="step === 2"
            v-close-popup
            color="black"
            :disabled="!validForm"
            :label="importLabel"
            no-caps
            @click="confirm"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import Papa from "papaparse";
import VueJsonCsv from "vue-json-csv";

import Modal from "shared/components/core/modals/Modal.vue";
import { getSources } from "shared/helpers/media";
import InsightsQuery from "shared/resources/InsightsQuery";

const ALLOWED_TOPIC_COLUMNS = ["category", "label", "boolean_query"];

export default {
  name: "ImportInsightsQueriesModal",
  components: {
    Modal,
    VueJsonCsv,
  },
  props: {
    organisationId: {
      type: [Number, String],
      required: true,
    },
    reportType: {
      type: String,
      required: true,
    },
    topicQuery: {
      type: Boolean,
    },
  },
  emits: ["cancel", "confirm"],
  data() {
    return {
      step: 1,
      queriesCSV: [
        {
          category: "Query Category",
          label: "Query Label",
          notes: "Notes. If empty it will be pre-filled with the filename",
          boolean_query: "this AND that",
          case_sensitive: false,
          print_content: true,
          magazine_content: false,
          podcast_content: false,
          online_content: true,
          tv_content: true,
          radio_content: true,
          social_content: false,
          filter_set_ids: "1;2;3",
          international_plus: false,
        },
      ],
      topicsCSV: [
        {
          category: "Topic Category",
          label: "Topic Label",
          boolean_query: "this AND that",
        },
      ],
      queries: [],
    };
  },
  computed: {
    sampleCSV() {
      return this.topicQuery ? this.topicsCSV : this.queriesCSV;
    },
    label() {
      return this.topicQuery ? "Topics" : "Queries";
    },
    validForm() {
      return this.queries.length && !this.queries.find((query) => !query.valid);
    },
    validateLabel() {
      return `Validate ${this.label}`;
    },
    importLabel() {
      return `Import ${this.label}`;
    },
    mediaTypes() {
      return getSources().map(({ medium }) => medium);
    },
  },
  methods: {
    processCsvFile([file]) {
      const reader = new FileReader();

      reader.onload = (event) => {
        const csvFile = event.target.result.trim();
        const parsed = Papa.parse(csvFile).data;
        const columns = parsed.shift();

        this.queries = parsed.map((values) => {
          // set columns uploaded
          const query = values.reduce((acc, value, index) => {
            if (
              this.topicQuery &&
              !ALLOWED_TOPIC_COLUMNS.includes(columns[index])
            ) {
              return acc;
            }

            let columnValue = value;
            if (["TRUE", "true"].includes(columnValue)) columnValue = true;
            if (["FALSE", "false"].includes(columnValue)) columnValue = false;

            if (value) acc[columns[index]] = columnValue;

            return acc;
          }, {});

          if (!query.notes?.length) {
            query.notes = file.name;
          }

          query.loading = false;
          query.validationMessage = "";
          query.valid = false;

          return query;
        });
      };

      reader.readAsText(file);
    },
    cancel() {
      this.$emit("cancel");
    },
    validateQueries() {
      this.step = 2;
      this.queries.forEach((query) => this.validateQuery(query));
    },
    async validateQuery(query) {
      try {
        Object.assign(query, { loading: true });

        await this.$streemApiAdmin.post("search", {
          params: {
            limit: 0,
            query: query.boolean_query,
            media: this.mediaTypes,
          },
        });

        Object.assign(query, { valid: true });
      } catch (error) {
        Object.assign(query, {
          valid: false,
          validationMessage: `Error: ${error.response.data.error.message.replace(
            '"\\\\"',
            '""'
          )}`,
        });
      } finally {
        Object.assign(query, { loading: false });
      }
    },
    typesForQuery(query) {
      return {
        caseSensitive: query.case_sensitive,
        printContent: query.print_content,
        magazineContent: query.magazine_content,
        onlineContent: query.online_content,
        radioContent: query.radio_content,
        tvContent: query.tv_content,
        socialContent: query.social_content,
        podcastContent: query.podcast_content,
      };
    },
    async confirm() {
      try {
        this.$q.loading.show();

        const promises = this.queries.map((query) => {
          const newQuery = new InsightsQuery({
            queryType: this.reportType,
            label: query.label,
            booleanQuery: query.boolean_query,
            category: query.category,
            notes: query.notes,
            filterSetIds: query.filter_set_ids
              ? query.filter_set_ids.split(";").map((id) => Number(id))
              : [],
            topicQuery: this.topicQuery,
            parentId: this.organisationId,
            parentType: "Organisation",
            internationalPlus: query.international_plus,
            ...(this.topicQuery ? {} : this.typesForQuery(query)),
          });

          return newQuery.save();
        });

        await Promise.all(promises);
        this.$emit("confirm");
      } catch (error) {
        this.$error("Something went wrong.", error);
      } finally {
        this.$q.loading.hide();
      }
    },
  },
};
</script>
